import { useEffect, useState, type FormEvent } from "react"
import { Send01 } from "@untitledui/icons"
import { toast } from "sonner"
import { teamsApi } from "@/lib/api/teams"
import { ApiError } from "@/lib/api/client"
import { useQueryClient } from "@tanstack/react-query"
import { queryKeys } from "@/lib/api/query-keys"
import { Roles } from "@/lib/team/roles"
import { DesignDialog } from "@/components/ui/design-dialog"
import { DesignSelect } from "@/components/ui/design-select"
import { US_STATES } from "@/components/ui/us-state-select"
import {
  Field,
  onboardingInputClass,
  onboardingInputErrorClass,
} from "@/components/onboarding/onboarding-shell"
import { cx } from "@/lib/utils/cx"

interface AddBodyshopDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  // Existing dealerships shown as a multi-select chip group so the new
  // shop can be wired to any subset on creation.
  dealerships: Array<{ teamId: number; name: string; city?: string; state?: string }>
}

export function AddBodyshopDialog({
  open,
  onOpenChange,
  dealerships,
}: AddBodyshopDialogProps) {
  const queryClient = useQueryClient()

  const [name, setName] = useState("")
  const [city, setCity] = useState("")
  const [state, setState] = useState("")
  const [gmEmail, setGmEmail] = useState("")
  // routesTo defaults to all dealerships — the common case for groups.
  const [routesTo, setRoutesTo] = useState<Set<number>>(new Set())
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState<{
    name?: string
    city?: string
    state?: string
    gmEmail?: string
  }>({})

  useEffect(() => {
    if (open) {
      setName("")
      setCity("")
      setState("")
      setGmEmail("")
      setRoutesTo(new Set(dealerships.map((d) => d.teamId)))
      setErrors({})
    }
  }, [open, dealerships])

  const toggleRoute = (id: number) =>
    setRoutesTo((rs) => {
      const next = new Set(rs)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const next: typeof errors = {}
    if (!name.trim()) next.name = "Name is required"
    if (!city.trim()) next.city = "City is required"
    if (!state.trim()) next.state = "Pick a state"
    if (!gmEmail.trim()) {
      next.gmEmail = "GM email is required so they can access the body shop"
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(gmEmail.trim())) {
      next.gmEmail = "Enter a valid email"
    }
    if (Object.keys(next).length > 0) {
      setErrors(next)
      return
    }

    setSubmitting(true)
    try {
      const team = await teamsApi.create({
        name: name.trim(),
        type: "Bodyshop",
        city: city.trim(),
        state: state.trim(),
      })
      await teamsApi.invite(team.id, {
        email: gmEmail.trim(),
        role: Roles.GeneralManager,
      })
      for (const dealershipTeamId of routesTo) {
        await teamsApi.connectBodyshop(dealershipTeamId, {
          bodyshopName: name.trim(),
        })
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.all })
      const linked = routesTo.size
      toast.success(
        `${name.trim()} added · GM invite sent${
          linked > 0 ? ` · linked to ${linked} dealership${linked === 1 ? "" : "s"}` : ""
        }`,
      )
      onOpenChange(false)
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : "Couldn't add this body shop"
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <DesignDialog.Root open={open} onOpenChange={onOpenChange}>
      <DesignDialog.Content width="lg">
        <DesignDialog.Header>
          <DesignDialog.Title>Add a body shop</DesignDialog.Title>
          <DesignDialog.Description>
            New shop with its own team. Pick which dealerships route to it —
            you can change connections anytime.
          </DesignDialog.Description>
        </DesignDialog.Header>
        <form onSubmit={handleSubmit}>
          <DesignDialog.Body className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-[1.4fr_1fr] gap-3">
              <Field label="Body shop name" required error={errors.name}>
                <input
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value)
                    if (errors.name) setErrors((er) => ({ ...er, name: undefined }))
                  }}
                  placeholder="e.g. LaFontaine Collision"
                  maxLength={255}
                  className={cx(onboardingInputClass, errors.name && onboardingInputErrorClass)}
                  autoFocus
                />
              </Field>
              <Field label="City" required error={errors.city}>
                <input
                  value={city}
                  onChange={(e) => {
                    setCity(e.target.value)
                    if (errors.city) setErrors((er) => ({ ...er, city: undefined }))
                  }}
                  placeholder="e.g. Detroit"
                  maxLength={120}
                  className={cx(onboardingInputClass, errors.city && onboardingInputErrorClass)}
                />
              </Field>
            </div>
            <Field label="State" required error={errors.state}>
              <DesignSelect.Root
                value={state}
                onValueChange={(v) => {
                  setState(v)
                  if (errors.state) setErrors((er) => ({ ...er, state: undefined }))
                }}
              >
                <DesignSelect.Trigger
                  className={cx(
                    onboardingInputClass,
                    "text-left flex items-center justify-between",
                    !state && "text-zinc-400",
                    errors.state && onboardingInputErrorClass,
                  )}
                >
                  <DesignSelect.Value placeholder="Pick a state" />
                </DesignSelect.Trigger>
                <DesignSelect.Content>
                  {US_STATES.map((s) => (
                    <DesignSelect.Item key={s} value={s}>
                      {s}
                    </DesignSelect.Item>
                  ))}
                </DesignSelect.Content>
              </DesignSelect.Root>
            </Field>

            <Field
              label="GM email"
              required
              hint="They'll be invited as General Manager"
              error={errors.gmEmail}
            >
              <input
                type="email"
                value={gmEmail}
                onChange={(e) => {
                  setGmEmail(e.target.value)
                  if (errors.gmEmail) setErrors((er) => ({ ...er, gmEmail: undefined }))
                }}
                placeholder="manager@bodyshop.com"
                className={cx(onboardingInputClass, errors.gmEmail && onboardingInputErrorClass)}
              />
            </Field>

            {dealerships.length > 0 && (
              <div className="rounded-xl border border-zinc-200 bg-zinc-50/40 p-3 space-y-2.5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[12.5px] font-semibold text-zinc-700">
                      Connect to dealerships
                    </p>
                    <p className="text-[11.5px] text-zinc-500 mt-0.5">
                      Pick which dealerships send work to this shop.
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => setRoutesTo(new Set(dealerships.map((d) => d.teamId)))}
                      disabled={routesTo.size === dealerships.length}
                      className="h-7 px-2.5 rounded-lg text-[11.5px] font-semibold text-zinc-600 border border-zinc-200 bg-white hover:border-zinc-300 disabled:opacity-40 transition-all duration-150"
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      onClick={() => setRoutesTo(new Set())}
                      disabled={routesTo.size === 0}
                      className="h-7 px-2.5 rounded-lg text-[11.5px] font-semibold text-zinc-600 border border-zinc-200 bg-white hover:border-zinc-300 disabled:opacity-40 transition-all duration-150"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div className="space-y-1">
                  {dealerships.map((d) => {
                    const checked = routesTo.has(d.teamId)
                    const cs = [d.city, d.state].filter(Boolean).join(", ")
                    return (
                      <button
                        key={d.teamId}
                        type="button"
                        onClick={() => toggleRoute(d.teamId)}
                        className={cx(
                          "w-full flex items-center gap-3 p-2 rounded-lg text-left transition-all duration-150",
                          checked ? "bg-emerald-50/70 hover:bg-emerald-50" : "hover:bg-zinc-50",
                        )}
                      >
                        <span
                          className={cx(
                            "size-5 rounded-md border-2 grid place-items-center shrink-0 transition-all duration-150",
                            checked
                              ? "bg-emerald-500 border-emerald-500"
                              : "bg-white border-zinc-300",
                          )}
                          aria-hidden
                        >
                          {checked && (
                            <svg viewBox="0 0 12 12" className="size-3 text-white">
                              <path
                                d="M2.5 6.5L5 9l4.5-5"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p
                            className={cx(
                              "text-[13.5px] font-semibold truncate",
                              checked ? "text-emerald-900" : "text-zinc-900",
                            )}
                          >
                            {d.name}
                          </p>
                          {cs && (
                            <p
                              className={cx(
                                "text-[11.5px] truncate",
                                checked ? "text-emerald-700/70" : "text-zinc-400",
                              )}
                            >
                              {cs}
                            </p>
                          )}
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </DesignDialog.Body>
          <DesignDialog.Footer>
            <DesignDialog.Close asChild>
              <button
                type="button"
                className="h-12 px-5 rounded-xl text-[14px] font-semibold text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 transition-all duration-150"
              >
                Cancel
              </button>
            </DesignDialog.Close>
            <button
              type="submit"
              disabled={submitting}
              className="h-12 px-7 inline-flex items-center gap-2 rounded-xl bg-zinc-900 text-white text-[15px] font-semibold shadow-lg shadow-zinc-900/20 hover:bg-black hover:shadow-zinc-900/30 transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
            >
              <Send01 className="size-4" aria-hidden />
              {submitting ? "Adding…" : "Add body shop & invite GM"}
            </button>
          </DesignDialog.Footer>
        </form>
      </DesignDialog.Content>
    </DesignDialog.Root>
  )
}
